import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import * as api from "../api";
import { useToast } from "../components/Toast";
import Modal, { ModalActions } from "../components/Modal";
import { PlusIcon, EditIcon, TrashIcon } from "../components/Icons";

/**
 * Group Organigram — free-form, PowerPoint-style canvas.
 *
 *   • Each box has its own (x, y) position. Drag-to-move with mouse.
 *   • The pinned root stays at the top — pinned boxes ignore drag.
 *   • Connections live in their own table; any two boxes can be linked
 *     by entering Connect mode and clicking source then target.
 *   • SVG underlay renders arrows; click an arrow to select it (then
 *     Edit / Delete from the toolbar).
 *   • Auto-layout button runs a quick BFS from the pinned root and
 *     stamps tree-style coordinates on every node missing positions
 *     (or all of them, if the user explicitly re-runs it).
 */

// CANVAS_W / CANVAS_H are the *layout reference* dimensions used while
// auto-laying out boxes (so things stay centered). The rendered canvas
// itself sizes dynamically to whatever the current nodes occupy plus
// a small padding margin, so the user never sees empty scroll space.
const CANVAS_W = 3400;
const CANVAS_H = 1300;
const ROW_MAX_W = 3300;
const CANVAS_PAD = 16;      // padding around the content extent
const DEFAULT_W = 200;
const DEFAULT_PRIMARY_W = 260;
const DEFAULT_H = 70;
const DEFAULT_PRIMARY_H = 80;

const boxW = (n) => n.width || (n.kind === "primary" ? DEFAULT_PRIMARY_W : DEFAULT_W);
const boxH = (n) => n.height || (n.kind === "primary" ? DEFAULT_PRIMARY_H : DEFAULT_H);


export default function OrganigramPage({ canEdit = false, isAdmin = false }) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  // Banner (top header) text + subtitle come from Settings.
  const [banner, setBanner] = useState({ name: "", subtitle: "" });
  const [editingNode, setEditingNode] = useState(null);
  const [editingEdge, setEditingEdge] = useState(null);

  // Selection — separate sets so a node selection and an edge selection
  // never coexist (matches PPT behaviour). Multiple nodes can be picked
  // via shift-click; alignment ops act on whichever set is non-empty.
  const [selectedNodeIds, setSelectedNodeIds] = useState(() => new Set());
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [mode, setMode] = useState("idle");          // "idle" | "connect"
  const [connectFrom, setConnectFrom] = useState(null);

  // View controls
  const [locked, setLocked] = useState(true);   // start locked; user unlocks to edit
  const [zoom, setZoom] = useState(1);          // 0.4 .. 2.0
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pageRef = useRef(null);                  // wrapper we send to fullscreen

  // Sync our isFullscreen state with the browser's fullscreen state — covers
  // ESC-to-exit and any external triggers.
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    const el = pageRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  // Effective edit privilege: needs canEdit AND view to be unlocked.
  const canMutate = canEdit && !locked;

  // Drag state (live, not persisted to backend until mouseup). Three
  // refs: moving boxes, resizing boxes, and dragging an edge elbow.
  const dragRef = useRef(null);     // { anchorId, pointerStart, starts, moved }
  const resizeRef = useRef(null);   // { id, startW, startH, startX, startY, kind, moved }
  const elbowDragRef = useRef(null);// { id, axis, moved }
  // Marquee (rubber-band) selection state — set when user mousedowns on
  // empty canvas and drags. Coords are in CANVAS-SCALED space (pixel/zoom).
  const marqueeRef = useRef(null);  // { startX, startY, moved }
  const [marqueeRect, setMarqueeRect] = useState(null);   // { left, top, width, height }
  // After a marquee drag the browser still fires a synthetic `click` on
  // mouseup, which would normally clear the selection. Set this flag in
  // mouseup so the click handler knows to ignore the next event.
  const suppressClickRef = useRef(false);
  const canvasRef = useRef(null);
  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    // Pull settings alongside the chart so the banner stays in sync.
    api.getSettings().then((s) => {
      setBanner({
        name: (s && s.header_company_name) || "",
        subtitle: (s && s.header_subtitle) || "",
      });
    }).catch(() => {});
    api.getOrganigram()
      .then((res) => {
        // Accept either the new shape ({ nodes, edges }) or the legacy
        // shape (just an array of nodes), so the page keeps working
        // even if the backend hasn't been restarted yet.
        let n = [];
        let e = [];
        if (Array.isArray(res)) {
          n = res;
        } else if (res && typeof res === "object") {
          n = Array.isArray(res.nodes) ? res.nodes : [];
          e = Array.isArray(res.edges) ? res.edges : [];
        }
        setNodes(n);
        setEdges(e);
        // Bootstrap auto-layout only when coordinates are missing.
        // The canvas applies a visual offset so any concrete (x, y) —
        // including negative or huge — renders correctly.
        const needsLayout = n.some((x) =>
          x.pos_x == null || x.pos_y == null
          || isNaN(x.pos_x) || isNaN(x.pos_y));
        if (needsLayout) {
          const laid = autoLayout(n, e);
          setNodes(laid);
          if (canEdit) {
            laid.forEach((nd) => {
              if (nd.is_pinned) {
                // Pinned nodes use the full PUT (the position-only route
                // 409s on pinned). Best-effort, silent on failure.
                api.updateOrganigramNode(nd.id, { pos_x: nd.pos_x, pos_y: nd.pos_y }).catch(() => {});
              } else {
                api.setOrganigramPosition(nd.id, nd.pos_x, nd.pos_y).catch(() => {});
              }
            });
          }
        }
      })
      .catch(() => toast("Failed to load organigram", "error"))
      .finally(() => setLoading(false));
  }, [toast, canEdit]);

  useEffect(() => { load(); }, [load]);

  const nodesById = useMemo(() => {
    const m = new Map();
    nodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [nodes]);

  // Canvas extent: true bounding box of all nodes plus a padding margin
  // on each side. `ox`/`oy` are the visual translation we apply at
  // render time so the leftmost / topmost box always sits at CANVAS_PAD,
  // no matter what its stored pos_x / pos_y is. That kills any empty
  // scroll area on the left or top.
  const extent = useMemo(() => {
    if (!nodes.length) return { w: 600, h: 400, ox: 0, oy: 0 };
    let minL = Infinity, minT = Infinity, maxR = 0, maxB = 0;
    nodes.forEach((n) => {
      const l = n.pos_x || 0, t = n.pos_y || 0;
      const r = l + boxW(n), b = t + boxH(n);
      if (l < minL) minL = l;
      if (t < minT) minT = t;
      if (r > maxR) maxR = r;
      if (b > maxB) maxB = b;
    });
    return {
      w: Math.ceil(maxR - minL + 2 * CANVAS_PAD),
      h: Math.ceil(maxB - minT + 2 * CANVAS_PAD),
      ox: CANVAS_PAD - minL,
      oy: CANVAS_PAD - minT,
    };
  }, [nodes]);

  // Freeze the visual offset while a drag/resize is in progress so the
  // unaffected boxes don't appear to lurch when the dragged box becomes
  // the new leftmost/topmost. Recomputes on drop.
  const [frozenOffset, setFrozenOffset] = useState(null);
  const renderOX = frozenOffset ? frozenOffset.ox : extent.ox;
  const renderOY = frozenOffset ? frozenOffset.oy : extent.oy;

  /* ─── Drag-to-move ───────────────────────────────────────────── */
  const onBoxMouseDown = (n) => (e) => {
    if (!canMutate || n.is_pinned) return;
    if (mode === "connect") return;
    if (e.button !== 0) return;
    // If the user starts dragging a box that's part of a multi-selection,
    // capture every selected node's starting position so they all move
    // together by the same delta (PPT-style group drag).
    const rect = canvasRef.current.getBoundingClientRect();
    const groupIds = selectedNodeIds.has(n.id) && selectedNodeIds.size > 1
      ? [...selectedNodeIds] : [n.id];
    const starts = new Map();
    groupIds.forEach((id) => {
      const nd = nodesById.get(id);
      if (nd && !nd.is_pinned) {
        starts.set(id, { x: nd.pos_x || 0, y: nd.pos_y || 0 });
      }
    });
    if (starts.size === 0) return;
    dragRef.current = {
      anchorId: n.id,
      pointerStart: {
        // World coord = pixel/zoom; we compare against world starts.
        x: (e.clientX - rect.left) / zoom,
        y: (e.clientY - rect.top) / zoom,
      },
      starts,
      moved: false,
    };
    setFrozenOffset({ ox: extent.ox, oy: extent.oy });
    e.preventDefault();
  };

  /* ─── Marquee (rubber-band) selection ──────────────────────────
     User drags from an empty area of the canvas to draw a rectangle.
     On release, every node whose centre falls inside the rectangle
     is added to selectedNodeIds. Holding Shift extends the existing
     selection; without Shift the selection is replaced.

     The handler is attached to the canvas's onMouseDown. React event
     bubbling means box / SVG-edge mousedowns fire FIRST — those set
     dragRef / resizeRef / elbowDragRef, which we check below to
     bail out so marquee only kicks in on truly empty canvas. */
  const onCanvasMouseDown = (e) => {
    if (!canMutate) return;
    if (mode === "connect") return;
    if (e.button !== 0) return;
    // Another handler claimed the gesture (box drag, resize, elbow).
    if (dragRef.current || resizeRef.current || elbowDragRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / zoom;
    const py = (e.clientY - rect.top) / zoom;
    marqueeRef.current = {
      startX: px, startY: py,
      shiftKey: !!e.shiftKey,
      // Snapshot existing selection so Shift+marquee extends it.
      baseline: e.shiftKey ? new Set(selectedNodeIds) : new Set(),
      moved: false,
    };
    setMarqueeRect({ left: px, top: py, width: 0, height: 0 });
    e.preventDefault();
  };

  const onCanvasMouseMove = (e) => {
    // Marquee — update rect + live-update selection.
    const mq = marqueeRef.current;
    if (mq) {
      const rect = canvasRef.current.getBoundingClientRect();
      const px = (e.clientX - rect.left) / zoom;
      const py = (e.clientY - rect.top) / zoom;
      mq.moved = true;
      const left = Math.min(mq.startX, px);
      const top  = Math.min(mq.startY, py);
      const width  = Math.abs(px - mq.startX);
      const height = Math.abs(py - mq.startY);
      setMarqueeRect({ left, top, width, height });
      // Compute selection in real-time. Box centres are in canvas-scaled
      // space too (n.pos_x + renderOX), so the comparison uses the same
      // coordinate system as the marquee rect.
      const right = left + width, bottom = top + height;
      const hits = new Set(mq.baseline);
      nodes.forEach((n) => {
        const cx = (n.pos_x || 0) + renderOX + boxW(n) / 2;
        const cy = (n.pos_y || 0) + renderOY + boxH(n) / 2;
        if (cx >= left && cx <= right && cy >= top && cy <= bottom) {
          hits.add(n.id);
        }
      });
      setSelectedNodeIds(hits);
      setSelectedEdgeId(null);
      return;
    }
    // Resize first — pixel deltas divided by zoom yield world deltas.
    const r = resizeRef.current;
    if (r) {
      const dx = (e.clientX - r.startX) / zoom;
      const dy = (e.clientY - r.startY) / zoom;
      const newW = r.kind === "b"
        ? r.startW
        : Math.max(120, Math.min(800, r.startW + dx));
      const newH = r.kind === "r"
        ? r.startH
        : Math.max(40, Math.min(400, r.startH + dy));
      r.moved = true;
      setNodes((prev) => prev.map((n) =>
        n.id === r.id ? { ...n, width: Math.round(newW), height: Math.round(newH) } : n
      ));
      return;
    }
    // Edge elbow drag
    const eb = elbowDragRef.current;
    if (eb) {
      const rect = canvasRef.current.getBoundingClientRect();
      // World coord of the cursor: undo the visual offset + zoom.
      const wx = (e.clientX - rect.left) / zoom - renderOX;
      const wy = (e.clientY - rect.top) / zoom - renderOY;
      eb.moved = true;
      const newBend = eb.axis === "y" ? wy : wx;
      setEdges((prev) => prev.map((x) =>
        x.id === eb.id ? { ...x, bend: newBend } : x
      ));
      return;
    }
    // Move (single or group)
    const d = dragRef.current;
    if (!d) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / zoom;
    const py = (e.clientY - rect.top) / zoom;
    const dx = px - d.pointerStart.x;
    const dy = py - d.pointerStart.y;
    d.moved = true;
    setNodes((prev) => prev.map((n) => {
      const start = d.starts.get(n.id);
      if (!start) return n;
      return { ...n, pos_x: start.x + dx, pos_y: start.y + dy };
    }));
  };

  const onCanvasMouseUp = () => {
    // Marquee end — selection was already updated on move; just clear UI state.
    const mq = marqueeRef.current;
    if (mq) {
      marqueeRef.current = null;
      setMarqueeRect(null);
      if (mq.moved) {
        // Keep the selection that grew during the drag. Suppress the
        // synthetic click that follows so onCanvasClick doesn't wipe it.
        suppressClickRef.current = true;
      } else if (!mq.shiftKey) {
        // Click-without-drag on empty canvas — fall through to the old
        // "clear selection" behaviour handled by onCanvasClick.
      }
      return;
    }
    const r = resizeRef.current;
    if (r) {
      resizeRef.current = null;
      setFrozenOffset(null);
      if (r.moved) {
        const updated = nodes.find((n) => n.id === r.id);
        if (updated) {
          api.updateOrganigramNode(updated.id, {
            width: updated.width, height: updated.height,
          }).catch((err) => toast(err.message || "Failed to save size", "error"));
        }
      }
      return;
    }
    const eb = elbowDragRef.current;
    if (eb) {
      elbowDragRef.current = null;
      setFrozenOffset(null);
      if (eb.moved) {
        const updated = edges.find((x) => x.id === eb.id);
        if (updated) {
          api.updateOrganigramEdge(updated.id, { bend: updated.bend })
            .catch((err) => toast(err.message || "Failed to save bend", "error"));
        }
      }
      return;
    }
    const d = dragRef.current;
    if (!d) {
      setFrozenOffset(null);
      return;
    }
    dragRef.current = null;
    setFrozenOffset(null);
    if (!d.moved) return;
    d.starts.forEach((_, id) => {
      const moved = nodes.find((n) => n.id === id);
      if (!moved) return;
      const persist = moved.is_pinned
        ? api.updateOrganigramNode(moved.id, { pos_x: moved.pos_x, pos_y: moved.pos_y })
        : api.setOrganigramPosition(moved.id, moved.pos_x, moved.pos_y);
      persist.catch(() => {});
    });
  };

  /* ─── Selection / connect-mode click ────────────────────────── */
  const onBoxClick = (n) => (e) => {
    e.stopPropagation();
    if (mode === "connect") {
      if (!connectFrom) { setConnectFrom(n.id); return; }
      if (connectFrom === n.id) { setConnectFrom(null); return; }
      api.addOrganigramEdge({ from_id: connectFrom, to_id: n.id, style: "solid" })
        .then((res) => {
          setEdges((p) => [...p, { id: res.id, from_id: connectFrom, to_id: n.id, style: "solid", label: "" }]);
          toast("Connected");
        })
        .catch((err) => toast(err.message || "Failed to connect", "error"))
        .finally(() => { setConnectFrom(null); setMode("idle"); });
      return;
    }
    if (dragRef.current && dragRef.current.moved) return;
    setSelectedEdgeId(null);
    if (e.shiftKey) {
      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        if (next.has(n.id)) next.delete(n.id); else next.add(n.id);
        return next;
      });
    } else {
      setSelectedNodeIds(new Set([n.id]));
    }
  };

  const onEdgeClick = (eg) => (e) => {
    e.stopPropagation();
    setSelectedNodeIds(new Set());
    setSelectedEdgeId(eg.id);
  };

  const onCanvasClick = () => {
    if (mode === "connect") { setConnectFrom(null); setMode("idle"); return; }
    // Marquee just ended with a real drag — keep the selection it built.
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setSelectedNodeIds(new Set());
    setSelectedEdgeId(null);
  };

  /* ─── Toolbar actions ────────────────────────────────────────── */
  const handleAddBox = () => {
    setEditingNode({
      mode: "add", id: null,
      name: "", subtitle: "", status: "", kind: "entity",
      edge_label: "", edge_style: "solid", note: "",
      width: 200, height: "",
      pos_x: 200, pos_y: 200,
      is_pinned: false,
    });
  };

  const handleEditSelection = () => {
    if (selectedEdgeId) {
      const eg = edges.find((x) => x.id === selectedEdgeId);
      if (eg) setEditingEdge({ ...eg });
      return;
    }
    if (selectedNodeIds.size === 1) {
      const n = nodesById.get([...selectedNodeIds][0]);
      if (!n) return;
      setEditingNode({
        mode: "edit", id: n.id,
        name: n.name || "", subtitle: n.subtitle || "",
        status: n.status || "", kind: n.kind || "entity",
        edge_label: n.edge_label || "", edge_style: n.edge_style || "solid",
        note: n.note || "",
        width: n.width || 200, height: n.height || "",
        pos_x: n.pos_x ?? 0, pos_y: n.pos_y ?? 0,
        is_pinned: !!n.is_pinned,
      });
    }
  };

  const deleteEdgeById = async (eid) => {
    try {
      await api.deleteOrganigramEdge(eid);
      setEdges((p) => p.filter((x) => x.id !== eid));
      if (selectedEdgeId === eid) setSelectedEdgeId(null);
      toast("Connection deleted");
    } catch (e) { toast(e.message, "error"); }
  };

  const handleDeleteSelection = async () => {
    if (selectedEdgeId) {
      await deleteEdgeById(selectedEdgeId);
      return;
    }
    if (selectedNodeIds.size === 0) return;
    const ids = [...selectedNodeIds];
    const targets = ids.map((id) => nodesById.get(id)).filter(Boolean);
    const deletable = targets.filter((n) => !n.is_pinned);
    const pinnedCount = targets.length - deletable.length;
    if (deletable.length === 0) {
      toast("Selected box is pinned. Unpin from Edit first.", "error");
      return;
    }
    const namesPreview = deletable.slice(0, 3).map((n) => `"${n.name}"`).join(", ")
      + (deletable.length > 3 ? `, +${deletable.length - 3} more` : "");
    const msg = deletable.length === 1
      ? `Delete ${namesPreview} (and any descendants)?`
      : `Delete ${deletable.length} boxes (${namesPreview}) and their descendants?`;
    if (!window.confirm(msg + (pinnedCount ? `\n\n${pinnedCount} pinned box(es) will be skipped.` : ""))) return;
    try {
      for (const n of deletable) {
        await api.deleteOrganigramNode(n.id);
      }
      toast(deletable.length === 1 ? "Node deleted" : `${deletable.length} nodes deleted`);
      setSelectedNodeIds(new Set());
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  /* ─── Alignment ops on the multi-selected nodes ──────────────── */
  const alignSelected = async (kind) => {
    if (selectedNodeIds.size < 2) return;
    const sel = [...selectedNodeIds]
      .map((id) => nodesById.get(id))
      .filter(Boolean)
      .map((n) => ({ ...n }));
    const xs = sel.map((n) => n.pos_x || 0);
    const ys = sel.map((n) => n.pos_y || 0);
    const rights = sel.map((n) => (n.pos_x || 0) + boxW(n));
    const bottoms = sel.map((n) => (n.pos_y || 0) + boxH(n));
    switch (kind) {
      case "left": {
        const t = Math.min(...xs);
        sel.forEach((n) => { n.pos_x = t; });
        break;
      }
      case "right": {
        const t = Math.max(...rights);
        sel.forEach((n) => { n.pos_x = t - boxW(n); });
        break;
      }
      case "top": {
        const t = Math.min(...ys);
        sel.forEach((n) => { n.pos_y = t; });
        break;
      }
      case "bottom": {
        const t = Math.max(...bottoms);
        sel.forEach((n) => { n.pos_y = t - boxH(n); });
        break;
      }
      case "center-h": {
        // Same vertical centerline (centers share X).
        const avg = sel.reduce((s, n) => s + (n.pos_x || 0) + boxW(n) / 2, 0) / sel.length;
        sel.forEach((n) => { n.pos_x = avg - boxW(n) / 2; });
        break;
      }
      case "center-v": {
        const avg = sel.reduce((s, n) => s + (n.pos_y || 0) + boxH(n) / 2, 0) / sel.length;
        sel.forEach((n) => { n.pos_y = avg - boxH(n) / 2; });
        break;
      }
      case "distribute-h": {
        if (sel.length < 3) { toast("Pick 3+ boxes to distribute", "error"); return; }
        sel.sort((a, b) => (a.pos_x || 0) - (b.pos_x || 0));
        const minL = sel[0].pos_x;
        const maxL = sel[sel.length - 1].pos_x;
        const step = (maxL - minL) / (sel.length - 1);
        sel.forEach((n, i) => { n.pos_x = minL + step * i; });
        break;
      }
      case "distribute-v": {
        if (sel.length < 3) { toast("Pick 3+ boxes to distribute", "error"); return; }
        sel.sort((a, b) => (a.pos_y || 0) - (b.pos_y || 0));
        const minT = sel[0].pos_y;
        const maxT = sel[sel.length - 1].pos_y;
        const step = (maxT - minT) / (sel.length - 1);
        sel.forEach((n, i) => { n.pos_y = minT + step * i; });
        break;
      }
      default: return;
    }
    const updates = new Map(sel.map((n) => [n.id, { pos_x: n.pos_x, pos_y: n.pos_y }]));
    setNodes((prev) => prev.map((n) =>
      updates.has(n.id) ? { ...n, ...updates.get(n.id) } : n));
    for (const n of sel) {
      try {
        if (n.is_pinned) {
          await api.updateOrganigramNode(n.id, { pos_x: n.pos_x, pos_y: n.pos_y });
        } else {
          await api.setOrganigramPosition(n.id, n.pos_x, n.pos_y);
        }
      } catch {}
    }
  };

  const handleAutoLayout = async () => {
    if (!window.confirm("Re-arrange every box into a tree layout? Box positions will be overwritten.")) return;
    const laid = autoLayout(nodes, edges, /*forceAll=*/true);
    setNodes(laid);
    // Persist. Pinned nodes go through the full PUT endpoint (which
    // accepts position updates regardless of pinned status); everything
    // else uses the lightweight position route.
    for (const n of laid) {
      try {
        if (n.is_pinned) {
          await api.updateOrganigramNode(n.id, { pos_x: n.pos_x, pos_y: n.pos_y });
        } else {
          await api.setOrganigramPosition(n.id, n.pos_x, n.pos_y);
        }
      } catch {}
    }
    toast("Auto-layout applied");
  };

  /* ─── Save modal ────────────────────────────────────────────── */
  const saveNode = async () => {
    if (!editingNode || !editingNode.name.trim()) return;
    const payload = {
      name: editingNode.name.trim(),
      subtitle: editingNode.subtitle.trim(),
      status: editingNode.status || null,
      kind: editingNode.kind || "entity",
      edge_label: editingNode.edge_label.trim(),
      edge_style: editingNode.edge_style || "solid",
      note: editingNode.note.trim(),
      width: parseInt(editingNode.width) || null,
      height: editingNode.height ? parseInt(editingNode.height) : null,
      pos_x: parseFloat(editingNode.pos_x) || 0,
      pos_y: parseFloat(editingNode.pos_y) || 0,
      is_pinned: !!editingNode.is_pinned,
    };
    try {
      if (editingNode.mode === "add") {
        await api.addOrganigramNode(payload);
        toast("Box added");
      } else {
        await api.updateOrganigramNode(editingNode.id, payload);
        toast("Box updated");
      }
      setEditingNode(null);
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  const saveEdge = async () => {
    if (!editingEdge) return;
    try {
      await api.updateOrganigramEdge(editingEdge.id, {
        style: editingEdge.style || "solid",
        label: (editingEdge.label || "").trim(),
        routing: editingEdge.routing || "auto",
      });
      // Server clears bend when routing changes — mirror that locally so
      // the connector re-centers immediately along the new axis.
      const wasRoutingChanged = editingEdge.routing && (() => {
        const orig = edges.find((x) => x.id === editingEdge.id);
        return orig && (orig.routing || "auto") !== editingEdge.routing;
      })();
      setEdges((p) => p.map((e) => e.id === editingEdge.id
        ? { ...e, ...editingEdge, bend: wasRoutingChanged ? null : e.bend }
        : e));
      toast("Connection updated");
      setEditingEdge(null);
    } catch (e) { toast(e.message, "error"); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  const onlySelectedNode = (selectedNodeIds.size === 1 && !selectedEdgeId)
    ? nodesById.get([...selectedNodeIds][0]) : null;
  const onlySelectedEdge = (selectedEdgeId && selectedNodeIds.size === 0)
    ? edges.find((e) => e.id === selectedEdgeId) : null;
  const selectionCount = selectedNodeIds.size + (selectedEdgeId ? 1 : 0);
  const hasSelection = selectionCount > 0;

  return (
    <div className="fade-in" ref={pageRef} style={{
      // In fullscreen, the wrapper itself becomes the viewport. Apply a
      // matching background and let inner cards fill the space.
      background: isFullscreen ? "var(--bg-primary, #0a0e1a)" : undefined,
      padding: isFullscreen ? 24 : undefined,
      overflowY: isFullscreen ? "auto" : undefined,
      height: isFullscreen ? "100vh" : undefined,
    }}>
      {/* Banner — driven by Settings → header_company_name + header_subtitle.
          Title + subtitle are inline on one line for a low-profile header. */}
      <div className="card mb-10" style={{
        background: "#0F2447", padding: "8px 20px",
        border: "1px solid #1f3a6b", textAlign: "center",
      }}>
        <span className="fw-700" style={{ fontSize: 15, color: "#fff", letterSpacing: 0.4 }}>
          {banner.name || "Group Organigram"}
        </span>
        {banner.subtitle && (
          <span style={{ marginLeft: 10, color: "#a3b3d6", fontStyle: "italic", fontSize: 12 }}>
            ({banner.subtitle})
          </span>
        )}
      </div>

      {/* Toolbar — compact strip. Legend moved into the canvas. View
          controls (zoom / fullscreen / lock) sit on the right; editing
          actions appear only when canMutate. */}
      <div className="card mb-10" style={{
        padding: "4px 10px", display: "flex", alignItems: "center",
        gap: 6, flexWrap: "wrap",
      }}>
        <div style={{ flex: 1 }} />

        {/* Zoom — icon-only buttons */}
        <div className="flex items-center" style={{ gap: 2 }}>
          <button className="btn btn-sm btn-secondary"
            onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))}
            title="Zoom out" style={{ padding: "3px 8px" }}>−</button>
          <button className="btn btn-sm btn-secondary" onClick={() => setZoom(1)}
            title="Reset zoom to 100%"
            style={{ minWidth: 48, padding: "3px 6px", fontSize: 11 }}>{Math.round(zoom * 100)}%</button>
          <button className="btn btn-sm btn-secondary"
            onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))}
            title="Zoom in" style={{ padding: "3px 8px" }}>+</button>
        </div>

        {/* Fullscreen toggle — icon-only. */}
        <button className="btn btn-sm btn-secondary" onClick={toggleFullscreen}
          title={isFullscreen ? "Exit fullscreen (ESC)" : "Enter fullscreen"}
          style={{ padding: "3px 8px" }}>
          {isFullscreen ? "⤬" : "⛶"}
        </button>

        {canEdit && (
          <button className={`btn btn-sm ${locked ? "btn-secondary" : "btn-primary"}`}
            onClick={() => {
              if (!locked) {
                setSelectedNodeIds(new Set());
                setSelectedEdgeId(null);
                setMode("idle"); setConnectFrom(null);
              }
              setLocked((v) => !v);
            }}
            title={locked ? "Unlock to enable editing" : "Lock the view (disables editing)"}
            style={{ padding: "3px 8px" }}>
            {locked ? "🔒" : "🔓"}
          </button>
        )}

        {canMutate && (
          <>
            <span style={{ width: 1, height: 22, background: "var(--border-input)", margin: "0 2px" }} />
            <button className="btn btn-sm btn-secondary" onClick={handleAddBox} title="Add a new box">
              <PlusIcon /> Add Box
            </button>
            <button className={`btn btn-sm ${mode === "connect" ? "btn-primary" : "btn-secondary"}`}
              onClick={() => {
                if (mode === "connect") { setMode("idle"); setConnectFrom(null); }
                else {
                  setMode("connect"); setConnectFrom(null);
                  setSelectedNodeIds(new Set()); setSelectedEdgeId(null);
                }
              }}
              title="Connect two boxes with an arrow">
              {mode === "connect" ? "Cancel Connect" : "→ Connect"}
            </button>
            <button className="btn btn-sm btn-secondary"
              disabled={!onlySelectedNode && !onlySelectedEdge}
              onClick={handleEditSelection} title="Edit selected box / connection">
              <EditIcon /> Edit
            </button>
            <button className="btn btn-sm btn-secondary" disabled={!hasSelection}
              onClick={handleDeleteSelection}
              title={selectedNodeIds.size > 1 ? `Delete ${selectedNodeIds.size} selected` : "Delete selected"}>
              <TrashIcon /> Delete{selectedNodeIds.size > 1 ? ` (${selectedNodeIds.size})` : ""}
            </button>
            {/* Auto-Layout is destructive (overwrites every position), so
                it's restricted to Admins. Edit users can still drag boxes
                and persist via the position endpoints. */}
            {isAdmin && (
              <button className="btn btn-sm btn-secondary" onClick={handleAutoLayout}
                title="Auto-layout every box into a tidy tree (overwrites all positions — Admin only)">
                ⌗ Auto-Layout
              </button>
            )}
          </>
        )}
      </div>

      {/* Alignment toolbar — shows when 2+ nodes are selected */}
      {canMutate && selectedNodeIds.size >= 2 && (
        <div className="card mb-12" style={{
          padding: "6px 12px", display: "flex", alignItems: "center",
          gap: 6, flexWrap: "wrap",
          background: "rgba(124,143,245,0.08)",
          border: "1px solid rgba(124,143,245,0.3)",
        }}>
          <span className="text-xxs" style={{ color: "var(--text-secondary)", marginRight: 6 }}>
            <strong>{selectedNodeIds.size} boxes selected</strong> · Align:
          </span>
          <button className="btn btn-sm btn-secondary" onClick={() => alignSelected("left")} title="Align left edges">⫷ Left</button>
          <button className="btn btn-sm btn-secondary" onClick={() => alignSelected("center-h")} title="Same vertical centerline">⊟ Center H</button>
          <button className="btn btn-sm btn-secondary" onClick={() => alignSelected("right")} title="Align right edges">⫸ Right</button>
          <span style={{ width: 1, height: 18, background: "var(--border-input)", margin: "0 4px" }} />
          <button className="btn btn-sm btn-secondary" onClick={() => alignSelected("top")} title="Align top edges">⫶ Top</button>
          <button className="btn btn-sm btn-secondary" onClick={() => alignSelected("center-v")} title="Same horizontal midline">⊟ Middle</button>
          <button className="btn btn-sm btn-secondary" onClick={() => alignSelected("bottom")} title="Align bottom edges">⫶ Bottom</button>
          <span style={{ width: 1, height: 18, background: "var(--border-input)", margin: "0 4px" }} />
          <button className="btn btn-sm btn-secondary" disabled={selectedNodeIds.size < 3}
            onClick={() => alignSelected("distribute-h")} title="Spread evenly horizontally (3+ boxes)">↔ Distribute H</button>
          <button className="btn btn-sm btn-secondary" disabled={selectedNodeIds.size < 3}
            onClick={() => alignSelected("distribute-v")} title="Spread evenly vertically (3+ boxes)">↕ Distribute V</button>
        </div>
      )}

      {/* Status line */}
      {(mode === "connect" || hasSelection) && (
        <div className="card mb-12" style={{
          padding: "6px 14px", fontSize: 12, color: "var(--text-secondary)",
          background: mode === "connect" ? "rgba(96,165,250,0.08)" : undefined,
        }}>
          {mode === "connect" && (
            <>
              <strong>Connect mode</strong> · {connectFrom
                ? `Click the target box to draw an arrow from "${nodesById.get(connectFrom)?.name}".`
                : "Click the source box first."}
              {" "}<button className="btn-link text-xxs" onClick={() => { setMode("idle"); setConnectFrom(null); }}>cancel</button>
            </>
          )}
          {mode !== "connect" && onlySelectedNode && (
            <>Selected box: <strong>{onlySelectedNode.name}</strong>
              {onlySelectedNode.is_pinned && " · pinned"}
              {" · "}<span className="text-dim">tip: drag corner / edge to resize · shift-click to multi-select</span>
            </>
          )}
          {mode !== "connect" && selectedNodeIds.size > 1 && (
            <><strong>{selectedNodeIds.size} boxes selected</strong> · use the alignment bar above, or drag any one to move all together.</>
          )}
          {mode !== "connect" && onlySelectedEdge && (
            <>Selected connection: {nodesById.get(onlySelectedEdge.from_id)?.name} → {nodesById.get(onlySelectedEdge.to_id)?.name}
              {" · "}<button className="btn-link text-xxs" onClick={() => deleteEdgeById(onlySelectedEdge.id)}>delete</button>
            </>
          )}
        </div>
      )}

      {/* Canvas. The outer card holds the dotted-grid background and
          scroll bars. The inner div is the world-coord canvas, scaled
          by `zoom` and sized so the scroll area always equals the
          scaled content. */}
      <div className="card" style={{ padding: 0, overflow: "auto", position: "relative" }}>
        {/* Floating legend — overlays the top-left of the canvas, doesn't
            steal vertical space. Positioned over the dotted-grid surface. */}
        <div style={{
          position: "absolute", top: 10, left: 10, zIndex: 5,
          background: "rgba(15,19,32,0.85)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "5px 10px",
          backdropFilter: "blur(6px)",
          pointerEvents: "none",   /* don't intercept canvas clicks */
        }}>
          <Legend />
        </div>
        <div style={{
          width: Math.ceil(extent.w * zoom),
          height: Math.ceil(extent.h * zoom),
          background: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)",
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
        }}>
        <div
          ref={canvasRef}
          onMouseDown={canMutate ? onCanvasMouseDown : undefined}
          onMouseMove={canMutate ? onCanvasMouseMove : undefined}
          onMouseUp={canMutate ? onCanvasMouseUp : undefined}
          onMouseLeave={canMutate ? onCanvasMouseUp : undefined}
          onClick={onCanvasClick}
          style={{
            position: "relative",
            width: extent.w, height: extent.h,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
            cursor: mode === "connect" ? "crosshair" : (marqueeRect ? "crosshair" : "default"),
          }}>
          {/* SVG layer for edges. Everything inside the <g> is shifted
              by (renderOX, renderOY) so paths line up with the offset
              boxes below. */}
          <svg width={extent.w} height={extent.h}
            style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
            <defs>
              <marker id="arrow-blue" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#60a5fa" />
              </marker>
              <marker id="arrow-amber" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#facc15" />
              </marker>
              <marker id="arrow-sel" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#7c8ff5" />
              </marker>
            </defs>
            <g transform={`translate(${renderOX}, ${renderOY})`}>
            {edges.map((eg) => {
              const a = nodesById.get(eg.from_id);
              const b = nodesById.get(eg.to_id);
              if (!a || !b) return null;
              const route = computeEdgePath(a, b, eg.bend, eg.routing);
              if (!route) return null;
              const isSel = selectedEdgeId === eg.id;
              const isDashed = (eg.style || "solid") === "dashed";
              const stroke = isSel ? "#7c8ff5"
                : isDashed ? "#facc15" : "#60a5fa";
              const marker = isSel ? "url(#arrow-sel)"
                : isDashed ? "url(#arrow-amber)" : "url(#arrow-blue)";
              // Delete badge sits at the elbow midpoint so it never
              // overlaps with the label chip (which now lives above the
              // target box).
              const badgeX = route.badgeX;
              const badgeY = route.badgeY;
              return (
                <g key={eg.id} style={{ pointerEvents: "auto", cursor: "pointer" }}>
                  <path d={route.d} stroke="transparent" strokeWidth="14" fill="none"
                    onClick={onEdgeClick(eg)} />
                  <path d={route.d}
                    stroke={stroke} strokeWidth={isSel ? 2.4 : 1.8}
                    strokeDasharray={isDashed ? "6 4" : undefined}
                    fill="none"
                    markerEnd={marker}
                    onClick={onEdgeClick(eg)} />
                  {eg.label && (() => {
                    const chipW = eg.label.length * 7.2 + 16;
                    return (
                      <g onClick={onEdgeClick(eg)}>
                        <rect x={route.labelX} y={route.labelY - 9}
                              width={chipW} height={18} rx={9}
                              fill={isDashed ? "rgba(245,158,11,0.18)" : "rgba(96,165,250,0.18)"}
                              stroke={isDashed ? "rgba(245,158,11,0.55)" : "rgba(96,165,250,0.55)"} />
                        <text x={route.labelX + chipW / 2} y={route.labelY + 4}
                              fontSize="11" fontWeight="700" textAnchor="middle"
                              fontFamily="'JetBrains Mono', monospace"
                              fill={isDashed ? "#facc15" : "#60a5fa"}>
                          {eg.label}
                        </text>
                      </g>
                    );
                  })()}
                  {isSel && canMutate && (
                    <>
                      {/* Draggable elbow handle — drag to move the
                          horizontal/vertical segment of the connector. */}
                      <g style={{ cursor: route.handleAxis === "y" ? "ns-resize" : "ew-resize" }}
                         onMouseDown={(e) => {
                           e.stopPropagation();
                           e.preventDefault();
                           elbowDragRef.current = {
                             id: eg.id, axis: route.handleAxis, moved: false,
                           };
                           setFrozenOffset({ ox: extent.ox, oy: extent.oy });
                         }}>
                        <title>Drag to move this connector's elbow</title>
                        <rect x={route.handleX - 7} y={route.handleY - 7}
                          width={14} height={14} rx={3}
                          fill="#7c8ff5" stroke="#ffffff" strokeWidth="1.5" />
                        {route.handleAxis === "y" ? (
                          <>
                            <line x1={route.handleX - 3} y1={route.handleY - 2}
                              x2={route.handleX + 3} y2={route.handleY - 2}
                              stroke="#ffffff" strokeWidth="1.2" />
                            <line x1={route.handleX - 3} y1={route.handleY + 2}
                              x2={route.handleX + 3} y2={route.handleY + 2}
                              stroke="#ffffff" strokeWidth="1.2" />
                          </>
                        ) : (
                          <>
                            <line x1={route.handleX - 2} y1={route.handleY - 3}
                              x2={route.handleX - 2} y2={route.handleY + 3}
                              stroke="#ffffff" strokeWidth="1.2" />
                            <line x1={route.handleX + 2} y1={route.handleY - 3}
                              x2={route.handleX + 2} y2={route.handleY + 3}
                              stroke="#ffffff" strokeWidth="1.2" />
                          </>
                        )}
                      </g>
                      <g onClick={(e) => { e.stopPropagation(); deleteEdgeById(eg.id); }}
                         style={{ cursor: "pointer" }}>
                        <title>Delete this connection</title>
                        <circle cx={badgeX + 22} cy={badgeY} r={10}
                          fill="#ef4444" stroke="#fff" strokeWidth="1.5" />
                        <line x1={badgeX + 18} y1={badgeY - 4}
                          x2={badgeX + 26} y2={badgeY + 4}
                          stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                        <line x1={badgeX + 26} y1={badgeY - 4}
                          x2={badgeX + 18} y2={badgeY + 4}
                          stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                      </g>
                    </>
                  )}
                </g>
              );
            })}
            {/* Connect-mode preview line from source */}
            {mode === "connect" && connectFrom && (
              <ConnectPreview source={nodesById.get(connectFrom)}
                canvasRef={canvasRef}
                offsetX={renderOX} offsetY={renderOY} zoom={zoom} />
            )}
            </g>
          </svg>

          {/* Marquee — rendered above the SVG edges but below the boxes
              so it doesn't obscure box interactions. pointer-events:none
              lets the mouse pass through to the underlying canvas div. */}
          {marqueeRect && (
            <div style={{
              position: "absolute",
              left: marqueeRect.left,
              top: marqueeRect.top,
              width: marqueeRect.width,
              height: marqueeRect.height,
              background: "rgba(124,143,245,0.10)",
              border: "1px dashed rgba(124,143,245,0.65)",
              borderRadius: 3,
              pointerEvents: "none",
              zIndex: 4,
            }} />
          )}

          {/* Node boxes */}
          {nodes.map((n) => (
            <NodeBox
              key={n.id}
              node={n}
              renderOX={renderOX} renderOY={renderOY}
              isSelected={selectedNodeIds.has(n.id)}
              showResizeHandles={canMutate && selectedNodeIds.size === 1
                && selectedNodeIds.has(n.id) && !n.is_pinned}
              isConnectSource={connectFrom === n.id}
              connectMode={mode === "connect"}
              onMouseDown={onBoxMouseDown(n)}
              onClick={onBoxClick(n)}
              onResizeStart={(kind) => (e) => {
                e.stopPropagation();
                e.preventDefault();
                resizeRef.current = {
                  id: n.id,
                  startW: boxW(n),
                  startH: boxH(n),
                  startX: e.clientX,
                  startY: e.clientY,
                  kind,
                  moved: false,
                };
                setFrozenOffset({ ox: extent.ox, oy: extent.oy });
              }}
            />
          ))}
        </div>
        </div>
      </div>

      {canEdit && (
        <p className="text-xxs text-dim mt-12" style={{ textAlign: "center" }}>
          Tip · Click <strong>{locked ? "🔒 Locked" : "🔓 Unlocked"}</strong> to toggle editing ·
          {!locked && <> Drag boxes / arrow elbows · Shift-click to multi-select · Corner handles to resize · ×&nbsp;to delete an arrow ·</>}
          {" "}Zoom: <strong>− / + / 100%</strong>.
        </p>
      )}

      {/* Node modal */}
      {editingNode && (
        <Modal
          title={editingNode.mode === "edit" ? `Edit Box — ${editingNode.name}` : "Add Box"}
          width="720px"
          onClose={() => setEditingNode(null)}>
          <div className="form-group"><label className="form-label">Name <span className="text-accent">*</span></label>
            <input className="input" autoFocus value={editingNode.name}
              onChange={(e) => setEditingNode({ ...editingNode, name: e.target.value })}
              placeholder="e.g. USL, RRPL, Choicest Enterprises Ltd." />
          </div>
          <div className="form-group"><label className="form-label">Subtitle <span className="text-dim">(small text under the name; supports inline colors)</span></label>
            <input className="input" value={editingNode.subtitle}
              onChange={(e) => setEditingNode({ ...editingNode, subtitle: e.target.value })}
              placeholder="e.g. {g}City Centre, AltAir Eco Centre,{/g} {a}AltAir SeleQtions, The AltAir House{/a}" />
            <p className="text-xxs text-dim mt-4">
              Use <code className="mono" style={{ color: "var(--green-light)" }}>{"{g}…{/g}"}</code> for green and{" "}
              <code className="mono" style={{ color: "var(--orange-light)" }}>{"{a}…{/a}"}</code> for amber within the same line.
            </p>
          </div>
          <div className="grid-2 gap-12">
            <div className="form-group"><label className="form-label">Status <span className="text-dim">(default subtitle color)</span></label>
              <select className="input select" value={editingNode.status}
                onChange={(e) => setEditingNode({ ...editingNode, status: e.target.value })}>
                <option value="">— None (header) —</option>
                <option value="operational">Operational (green)</option>
                <option value="planning">Stage of Planning (amber)</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Kind</label>
              <select className="input select" value={editingNode.kind}
                onChange={(e) => setEditingNode({ ...editingNode, kind: e.target.value })}>
                <option value="primary">Primary (top-level header)</option>
                <option value="entity">Entity (standard box)</option>
                <option value="leaf">Leaf (location label)</option>
              </select>
            </div>
          </div>
          <div className="grid-2 gap-12">
            <div className="form-group"><label className="form-label">Width (px)</label>
              <input className="input mono" type="number" min="120" max="600" step="10"
                value={editingNode.width}
                onChange={(e) => setEditingNode({ ...editingNode, width: e.target.value })} />
            </div>
            <div className="form-group"><label className="form-label">Height (px) <span className="text-dim">(optional)</span></label>
              <input className="input mono" type="number" min="40" max="400" step="5"
                value={editingNode.height}
                onChange={(e) => setEditingNode({ ...editingNode, height: e.target.value })}
                placeholder="auto" />
            </div>
          </div>
          <div className="grid-2 gap-12">
            <div className="form-group"><label className="form-label">X position (px)</label>
              <input className="input mono" type="number" step="10"
                value={editingNode.pos_x}
                onChange={(e) => setEditingNode({ ...editingNode, pos_x: e.target.value })} />
            </div>
            <div className="form-group"><label className="form-label">Y position (px)</label>
              <input className="input mono" type="number" step="10"
                value={editingNode.pos_y}
                onChange={(e) => setEditingNode({ ...editingNode, pos_y: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label className="flex items-center gap-10" style={{ cursor: "pointer", padding: "8px 12px",
              borderRadius: 8, border: "1px solid var(--border-input)", background: "rgba(255,255,255,0.03)" }}>
              <input type="checkbox" checked={!!editingNode.is_pinned}
                onChange={(e) => setEditingNode({ ...editingNode, is_pinned: e.target.checked })}
                style={{ accentColor: "var(--accent)", width: 16, height: 16 }} />
              <span className="text-sm">Pinned (locked at this position; drag is disabled)</span>
            </label>
          </div>
          <div className="form-group"><label className="form-label">Note <span className="text-dim">(optional side annotation)</span></label>
            <input className="input" value={editingNode.note}
              onChange={(e) => setEditingNode({ ...editingNode, note: e.target.value })}
              placeholder="e.g. 14.29% Owned by KMC" />
          </div>
          <ModalActions
            onCancel={() => setEditingNode(null)}
            onConfirm={saveNode}
            confirmLabel={editingNode.mode === "edit" ? "Save Changes" : "Add Box"}
            disabled={!editingNode.name.trim()} />
        </Modal>
      )}

      {/* Edge modal */}
      {editingEdge && (
        <Modal title="Edit Connection" width="480px" onClose={() => setEditingEdge(null)}>
          <p className="text-sm text-secondary mb-12">
            <strong>{nodesById.get(editingEdge.from_id)?.name}</strong>{" → "}
            <strong>{nodesById.get(editingEdge.to_id)?.name}</strong>
          </p>
          <div className="form-group">
            <label className="form-label">Label <span className="text-dim">(e.g. 60%, (Proposed))</span></label>
            <input className="input" autoFocus value={editingEdge.label || ""}
              onChange={(e) => setEditingEdge({ ...editingEdge, label: e.target.value })}
              placeholder="e.g. 60%" />
          </div>
          <div className="form-group">
            <label className="form-label">Style</label>
            <select className="input select" value={editingEdge.style || "solid"}
              onChange={(e) => setEditingEdge({ ...editingEdge, style: e.target.value })}>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed (proposed)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Routing <span className="text-dim">(which side of each box the connector attaches to)</span></label>
            <select className="input select" value={editingEdge.routing || "auto"}
              onChange={(e) => setEditingEdge({ ...editingEdge, routing: e.target.value })}>
              <option value="auto">Auto (pick based on position)</option>
              <option value="tb">Top ↕ Bottom</option>
              <option value="lr">Left ↔ Right (sides)</option>
            </select>
            <p className="text-xxs text-dim mt-4">
              Changing the routing resets any manual elbow adjustment.
            </p>
          </div>
          <ModalActions
            onCancel={() => setEditingEdge(null)}
            onConfirm={saveEdge}
            confirmLabel="Save" />
        </Modal>
      )}
    </div>
  );
}


/* ─── Node box (absolute-positioned on the canvas) ───────────────── */
function NodeBox({ node: n, renderOX = 0, renderOY = 0,
                   isSelected, isConnectSource, connectMode,
                   showResizeHandles, onResizeStart,
                   onMouseDown, onClick }) {
  const isPrimary = n.kind === "primary";
  const isLeaf = n.kind === "leaf";
  const defaultColor = n.status === "operational" ? "#34d399"
    : n.status === "planning" ? "#facc15" : "#cbd5f5";
  const bg = isPrimary ? "#143b6b" : isLeaf ? "#f1f5f9" : "#0f2c54";
  const titleColor = isLeaf ? "#0f172a" : "#ffffff";
  let borderColor = isPrimary ? "#2563eb" : isLeaf ? "#cbd5e1" : "#1e3a6e";
  if (isSelected) borderColor = "#7c8ff5";
  if (isConnectSource) borderColor = "#fbbf24";

  return (
    <div
      onMouseDown={onMouseDown}
      onClick={onClick}
      style={{
        position: "absolute",
        left: (n.pos_x || 0) + renderOX,
        top: (n.pos_y || 0) + renderOY,
        width: boxW(n),
        height: n.height ? `${n.height}px` : undefined,
        minHeight: boxH(n),
        boxSizing: "border-box",
        background: bg,
        border: `${isSelected || isConnectSource ? 2 : 1}px solid ${borderColor}`,
        borderRadius: 6, padding: "8px 10px", textAlign: "center",
        cursor: connectMode ? "pointer" : (n.is_pinned ? "not-allowed" : "grab"),
        userSelect: "none",
        boxShadow: isSelected ? "0 0 0 3px rgba(124,143,245,0.25)"
          : isConnectSource ? "0 0 0 3px rgba(251,191,36,0.25)" : undefined,
        display: "flex", flexDirection: "column", justifyContent: "center",
        zIndex: isSelected ? 3 : 2,
      }}
      title={n.is_pinned ? "Pinned (drag is disabled)" : "Drag to move · Click to select"}>
      {n.is_pinned && (
        <span style={{
          position: "absolute", top: 4, right: 6,
          fontSize: 10, color: isLeaf ? "#475569" : "#cbd5f5", opacity: 0.7,
        }}>📌</span>
      )}
      <p className="fw-700" style={{
        color: titleColor, fontSize: isPrimary ? 13 : 12,
        lineHeight: 1.3, margin: 0, width: "100%",
        whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word",
      }}>{n.name}</p>
      {n.subtitle && (
        <ColoredSubtitle text={n.subtitle} isLeaf={isLeaf} defaultColor={defaultColor} />
      )}
      {n.note && (
        <p style={{
          fontSize: 10, fontStyle: "italic", lineHeight: 1.3,
          marginTop: 4, marginBottom: 0, width: "100%",
          color: isLeaf ? "#475569" : "#a3b3d6",
          opacity: 0.9,
          whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word",
        }}>({n.note})</p>
      )}
      {/* Resize handles (PPT-style). Right edge → width, bottom edge →
          height, bottom-right corner → both. */}
      {showResizeHandles && (
        <>
          <ResizeHandle kind="r" onMouseDown={onResizeStart("r")}
            style={{ right: -4, top: "50%", transform: "translateY(-50%)", cursor: "ew-resize" }} />
          <ResizeHandle kind="b" onMouseDown={onResizeStart("b")}
            style={{ left: "50%", bottom: -4, transform: "translateX(-50%)", cursor: "ns-resize" }} />
          <ResizeHandle kind="br" onMouseDown={onResizeStart("br")}
            style={{ right: -4, bottom: -4, cursor: "nwse-resize" }} />
        </>
      )}
    </div>
  );
}


/* ─── Small drag-handle dot on a selected box's border ───────────── */
function ResizeHandle({ onMouseDown, style }) {
  return (
    <div
      onMouseDown={onMouseDown}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute", width: 9, height: 9,
        background: "#7c8ff5", border: "1.5px solid #ffffff",
        borderRadius: 2, zIndex: 5,
        boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
        ...style,
      }} />
  );
}


/* ─── Inline color tags inside subtitle ─────────────────────────── */
function ColoredSubtitle({ text, isLeaf, defaultColor }) {
  const segments = useMemo(() => {
    const re = /\{([ga])\}([\s\S]*?)\{\/\1\}/gi;
    const out = [];
    let last = 0; let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push({ kind: "default", text: text.slice(last, m.index) });
      out.push({ kind: m[1].toLowerCase() === "g" ? "green" : "amber", text: m[2] });
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push({ kind: "default", text: text.slice(last) });
    return out;
  }, [text]);

  const colorOf = (kind) => {
    if (isLeaf) return "#475569";
    if (kind === "green") return "#34d399";
    if (kind === "amber") return "#facc15";
    return defaultColor;
  };

  return (
    <p style={{
      fontSize: 10.5, marginTop: 4, marginBottom: 0, lineHeight: 1.35,
      width: "100%", whiteSpace: "normal",
      wordBreak: "break-word", overflowWrap: "break-word",
    }}>
      {segments.map((s, i) => (
        <span key={i} style={{ color: colorOf(s.kind) }}>{s.text}</span>
      ))}
    </p>
  );
}


/* ─── Connect-mode preview line follows the cursor ──────────────── */
function ConnectPreview({ source, canvasRef, offsetX = 0, offsetY = 0, zoom = 1 }) {
  const [pt, setPt] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Pixel → unscaled canvas → world (subtract the <g> translate).
      setPt({
        x: (e.clientX - rect.left) / zoom - offsetX,
        y: (e.clientY - rect.top) / zoom - offsetY,
      });
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [canvasRef, offsetX, offsetY, zoom]);
  if (!source || !pt) return null;
  const sx = (source.pos_x || 0) + boxW(source) / 2;
  const sy = (source.pos_y || 0) + boxH(source) / 2;
  return (
    <line x1={sx} y1={sy} x2={pt.x} y2={pt.y}
      stroke="#fbbf24" strokeWidth="1.6" strokeDasharray="4 4"
      pointerEvents="none" />
  );
}


/* ─── Compute orthogonal (elbow) edge path between two boxes ──────
   Returns the SVG path plus two anchor points:
     labelX/labelY  — sits just above the target box (where the % chip
                      lives, matching the reference org chart).
     badgeX/badgeY  — the elbow midpoint (where the red × delete badge
                      goes when the edge is selected). */
function computeEdgePath(a, b, bend, routing = "auto") {
  const aLeft = a.pos_x || 0;
  const aTop = a.pos_y || 0;
  const aRight = aLeft + boxW(a);
  const aBottom = aTop + boxH(a);
  const aCx = aLeft + boxW(a) / 2;
  const aCy = aTop + boxH(a) / 2;

  const bLeft = b.pos_x || 0;
  const bTop = b.pos_y || 0;
  const bRight = bLeft + boxW(b);
  const bBottom = bTop + boxH(b);
  const bCx = bLeft + boxW(b) / 2;
  const bCy = bTop + boxH(b) / 2;

  const labelX = bLeft;
  const labelY = bTop - 14;

  const dy = bCy - aCy;
  const dx = bCx - aCx;

  // `routing` lets the user pin which ports a connector uses.
  //   "auto" → heuristic below (default)
  //   "tb"   → force top/bottom ports (vertical-first elbow)
  //   "lr"   → force left/right ports (horizontal-first elbow)
  const useVerticalFirst = routing === "tb"
    ? true
    : routing === "lr"
      ? false
      : Math.abs(dy) > Math.max(boxH(a), boxH(b)) * 0.6;

  // Vertical-first elbow (target clearly above or below)
  if (useVerticalFirst) {
    const sx = aCx, tx = bCx;
    const sy = dy > 0 ? aBottom : aTop;
    const ty = dy > 0 ? bTop : bBottom;
    // `bend` overrides the elbow Y; clamp so the elbow stays between
    // source and target so the line stays sensible.
    const defaultMidY = sy + (ty - sy) / 2;
    let midY = bend != null ? bend : defaultMidY;
    const lo = Math.min(sy, ty), hi = Math.max(sy, ty);
    midY = Math.max(lo, Math.min(hi, midY));
    const d = `M ${sx},${sy} L ${sx},${midY} L ${tx},${midY} L ${tx},${ty}`;
    return {
      d, labelX, labelY,
      badgeX: (sx + tx) / 2, badgeY: midY,
      // The horizontal segment between (sx,midY) and (tx,midY) is the
      // draggable elbow. Handle goes at its midpoint, drags along Y.
      handleX: (sx + tx) / 2, handleY: midY, handleAxis: "y",
    };
  }
  // Horizontal-first elbow (target roughly at same vertical level)
  const sy = aCy, ty = bCy;
  const sx = dx >= 0 ? aRight : aLeft;
  const tx = dx >= 0 ? bLeft : bRight;
  const defaultMidX = (sx + tx) / 2;
  let midX = bend != null ? bend : defaultMidX;
  const loX = Math.min(sx, tx), hiX = Math.max(sx, tx);
  midX = Math.max(loX, Math.min(hiX, midX));
  const d = `M ${sx},${sy} L ${midX},${sy} L ${midX},${ty} L ${tx},${ty}`;
  return {
    d, labelX, labelY,
    badgeX: midX, badgeY: (sy + ty) / 2,
    handleX: midX, handleY: (sy + ty) / 2, handleAxis: "x",
  };
}


/* ─── Auto-layout: row-packed tiers with parent-aware ordering.
   Behaviour matches the reference snapshot:
     • Children sit close to their parent's X position (siblings stay
       near the same parent).
     • Proposed companies (is_proposed=true) go to the extreme right
       within their tier — they're the dashed "(Proposed)" branches.
     • When a tier has too many nodes to fit one row, it wraps to a
       second/third row at the SAME tier (not pushed deeper).
*/
function autoLayout(nodes, edges, forceAll = false) {
  if (nodes.length === 0) return nodes;
  const byId = new Map(nodes.map((n) => [n.id, n]));

  // ── Adjacency (edges + legacy parent_id) ────────────────────────────
  const childrenOf = new Map();
  const parentOf = new Map();
  const addChild = (p, c) => {
    if (!byId.has(p) || !byId.has(c) || p === c) return;
    if (!childrenOf.has(p)) childrenOf.set(p, []);
    if (!childrenOf.get(p).includes(c)) childrenOf.get(p).push(c);
    if (!parentOf.has(c)) parentOf.set(c, p);   // first parent wins
  };
  edges.forEach((e) => addChild(e.from_id, e.to_id));
  nodes.forEach((n) => { if (n.parent_id) addChild(n.parent_id, n.id); });

  // Sort each parent's children: proposed last, then sort_order, then name.
  const siblingCmp = (a, b) => {
    const na = byId.get(a), nb = byId.get(b);
    if (!!na.is_proposed !== !!nb.is_proposed) return na.is_proposed ? 1 : -1;
    const so = (na.sort_order || 0) - (nb.sort_order || 0);
    if (so) return so;
    return (na.name || "").localeCompare(nb.name || "");
  };
  childrenOf.forEach((arr) => arr.sort(siblingCmp));

  // ── Roots ──────────────────────────────────────────────────────────
  const incoming = new Set();
  edges.forEach((e) => incoming.add(e.to_id));
  const roots = nodes
    .filter((n) => !incoming.has(n.id))
    .sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
      if ((a.kind || "") !== (b.kind || "")) {
        if (a.kind === "primary") return -1;
        if (b.kind === "primary") return 1;
      }
      if (!!a.is_proposed !== !!b.is_proposed) return a.is_proposed ? 1 : -1;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  if (!roots.length) roots.push(nodes[0]);

  // ── Depth via BFS from roots ───────────────────────────────────────
  const depthOf = new Map();
  const queue = [];
  roots.forEach((r) => { depthOf.set(r.id, 0); queue.push(r.id); });
  while (queue.length) {
    const cur = queue.shift();
    const d = depthOf.get(cur);
    (childrenOf.get(cur) || []).forEach((c) => {
      if (!depthOf.has(c)) {
        depthOf.set(c, d + 1);
        queue.push(c);
      }
    });
  }
  // Disconnected nodes pile at the bottom.
  let maxD = 0;
  depthOf.forEach((d) => { if (d > maxD) maxD = d; });
  nodes.forEach((n) => { if (!depthOf.has(n.id)) depthOf.set(n.id, maxD + 1); });

  // ── Group by depth, order each tier by parent's position above ─────
  const byDepth = new Map();
  nodes.forEach((n) => {
    const d = depthOf.get(n.id);
    if (!byDepth.has(d)) byDepth.set(d, []);
    byDepth.get(d).push(n.id);
  });

  const orderInTier = new Map();   // id → 0-based index in its tier
  const sortedDepths = Array.from(byDepth.keys()).sort((a, b) => a - b);

  // Tier 0 is sorted purely by sibling comparator.
  const tier0 = (byDepth.get(sortedDepths[0]) || []).slice().sort(siblingCmp);
  byDepth.set(sortedDepths[0], tier0);
  tier0.forEach((id, i) => orderInTier.set(id, i));

  // Subsequent tiers: sort by parent's index in previous tier, then
  // proposed-last, then sort_order. Keeps siblings adjacent and pushes
  // proposed branches (entire subtrees) to the right.
  sortedDepths.slice(1).forEach((d) => {
    const tier = byDepth.get(d).slice();
    tier.sort((a, b) => {
      const pa = parentOf.get(a);
      const pb = parentOf.get(b);
      const oa = pa != null ? (orderInTier.get(pa) ?? 9999) : 9999;
      const ob = pb != null ? (orderInTier.get(pb) ?? 9999) : 9999;
      if (oa !== ob) return oa - ob;
      return siblingCmp(a, b);
    });
    byDepth.set(d, tier);
    tier.forEach((id, i) => orderInTier.set(id, i));
  });

  // ── Place: each tier has a "regular lane" (centered, row-packed) and
  // a "proposed lane" (right-justified at canvas edge). Proposed always
  // share the FIRST row of the tier so they line up with the leftmost
  // regular nodes (matches snapshot — Navin Buildcon + Choicest at far
  // right alongside USL/VPPL/RRPL).
  const MAX_PER_ROW = 8;        // regulars per row before wrapping
  const X_GAP = 28;             // horizontal gap between siblings
  const ROW_GAP = 24;           // gap between wrapped rows in same tier
  const TIER_GAP = 110;         // gap between tiers
  const PROPOSED_LANE_GAP = 80; // gap between regular lane and proposed lane

  const out = nodes.map((n) => ({ ...n }));
  const updated = new Map(out.map((n) => [n.id, n]));

  const widthOf = (id) => boxW(updated.get(id));
  const heightOf = (id) => boxH(updated.get(id));
  const sumWithGaps = (ids) =>
    ids.reduce((s, id, i) => s + widthOf(id) + (i > 0 ? X_GAP : 0), 0);

  let cumulativeY = 10;
  sortedDepths.forEach((d) => {
    const tier = byDepth.get(d);
    if (!tier || !tier.length) return;

    // Split: proposed companies go in their own right lane.
    const regular = tier.filter((id) => !updated.get(id).is_proposed);
    const proposed = tier.filter((id) =>  updated.get(id).is_proposed);

    const proposedW = proposed.length ? sumWithGaps(proposed) : 0;

    // Row-pack regulars by count (MAX_PER_ROW per row). This matches the
    // snapshot's behaviour where USL/VPPL/RRPL/Rising Towers/CHVCL/Green
    // Emerald fill row 1 and Skylark/Epoch/GARPL/Urshita wrap to row 2.
    const rows = [];
    for (let i = 0; i < regular.length; i += MAX_PER_ROW) {
      rows.push(regular.slice(i, i + MAX_PER_ROW));
    }
    if (rows.length === 0) rows.push([]);   // ensure row 1 exists for proposed lane

    // Place row 1 first (knowing proposed needs to fit on its right).
    // Strategy: total span = (row1 width) + gap + (proposed width). Centre
    // that span horizontally so the chart still looks balanced.
    let rowY = cumulativeY;
    let lastRowMaxH = 0;

    rows.forEach((row, ri) => {
      const rowW = sumWithGaps(row);
      const isRow1 = ri === 0;
      const totalSpan = isRow1 && proposed.length
        ? rowW + PROPOSED_LANE_GAP + proposedW
        : rowW;

      let cx = Math.max(30, (CANVAS_W - totalSpan) / 2);
      const rowMaxH = row.reduce((m, id) => Math.max(m, heightOf(id)), 0);

      row.forEach((id) => {
        const n = updated.get(id);
        const skip = !forceAll && n.pos_x != null && n.pos_y != null
                     && !isNaN(n.pos_x) && !isNaN(n.pos_y);
        if (!skip) {
          n.pos_x = cx;
          n.pos_y = rowY;
        }
        cx += widthOf(id) + X_GAP;
      });

      // Place proposed on the same Y as row 1, with a visible gap.
      if (isRow1 && proposed.length) {
        // cx is now at the right edge of the regular row's content
        // (one X_GAP past the last regular). Skip the residual gap and
        // add the lane gap to create the "(Proposed)" visual break.
        let px = (cx - X_GAP) + PROPOSED_LANE_GAP;
        // If row 1 had no regulars, anchor proposed to the right of the canvas.
        if (row.length === 0) {
          px = Math.max(30, CANVAS_W - proposedW - 30);
        }
        proposed.forEach((id) => {
          const n = updated.get(id);
          const skip = !forceAll && n.pos_x != null && n.pos_y != null
                       && !isNaN(n.pos_x) && !isNaN(n.pos_y);
          if (!skip) {
            n.pos_x = px;
            n.pos_y = rowY;
          }
          px += widthOf(id) + X_GAP;
        });
        // Proposed boxes also contribute to row height.
        const propH = proposed.reduce((m, id) => Math.max(m, heightOf(id)), 0);
        const rowH = Math.max(rowMaxH, propH);
        if (ri < rows.length - 1) rowY += rowH + ROW_GAP;
        else lastRowMaxH = rowH;
        return;
      }

      if (ri < rows.length - 1) rowY += rowMaxH + ROW_GAP;
      else lastRowMaxH = rowMaxH;
    });

    cumulativeY = rowY + lastRowMaxH + TIER_GAP;
  });

  // Sanity: stamp a fallback for any node that ended up NaN/null.
  let fallbackX = 30;
  const fallbackY = cumulativeY + TIER_GAP;
  out.forEach((n) => {
    if (typeof n.pos_x !== "number" || isNaN(n.pos_x)
        || typeof n.pos_y !== "number" || isNaN(n.pos_y)) {
      n.pos_x = fallbackX;
      n.pos_y = fallbackY;
      fallbackX += boxW(n) + X_GAP;
    }
  });

  // Normalize: shift everything so the leftmost/topmost box sits at
  // CANVAS_PAD. Kills empty scroll regions.
  let minX = Infinity, minY = Infinity;
  out.forEach((n) => {
    if (n.pos_x < minX) minX = n.pos_x;
    if (n.pos_y < minY) minY = n.pos_y;
  });
  if (isFinite(minX) && isFinite(minY)) {
    const dx = CANVAS_PAD - minX;
    const dy = CANVAS_PAD - minY;
    out.forEach((n) => { n.pos_x += dx; n.pos_y += dy; });
  }
  return out;
}


function Legend() {
  return (
    <div className="flex items-center gap-16" style={{ flexWrap: "wrap" }}>
      <span className="flex items-center gap-6 text-xxs">
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#34d399", display: "inline-block" }} />
        Operational
      </span>
      <span className="flex items-center gap-6 text-xxs">
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#facc15", display: "inline-block" }} />
        Planning
      </span>
      <span className="flex items-center gap-6 text-xxs text-dim">
        <span style={{ width: 24, height: 0, borderTop: "2px dashed rgba(245,158,11,0.7)", display: "inline-block" }} />
        Proposed
      </span>
    </div>
  );
}
